import { createContext, SetStateAction, useContext, useState, useEffect } from "react";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import UserPool from "../services/aws/UserPool";

interface cognitoUser {
  email: string;
  password: string;
}

export interface cognitoIdentityCredentials {
  accessKeyId: string;
  secretKey: string;
  sessionToken: string;
  identityId: string;
}
export interface cognitoUserAttributes {
  sub: string | undefined;
  email_verified: string | undefined;
  email: string | undefined;
}

export interface userSubscriptionData {
  accountType: string;
  cancelAtPeriodEnd: boolean;
  createdOnDate: string;
  subscriptionStatus: string;
  admin: string;
  isAdminActive: boolean;
}

interface CognitoUserData {
  loggedInUserCognitoIdToken: string | undefined;
  setLoggedInUserCognitoIdToken: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;

  userCognitoAttrubutes: {};
  setUserCognitoAttrubutes: React.Dispatch<React.SetStateAction<{}>>;
  authenticate: (Username: string, Password: string) => Promise<unknown>;
  getSession: () => Promise<unknown>;
  getRefreshedToken: () => Promise<string>;
  logout: () => void;
  myAccountOpen: boolean;
  setMyAccountOpen: React.Dispatch<SetStateAction<boolean>>;
  subscriptionDetails: userSubscriptionData | null;
  setSubscriptionDetails: React.Dispatch<SetStateAction<userSubscriptionData | null>>;
  impersonatedUserId: string | null;
  setImpersonatedUserId: React.Dispatch<SetStateAction<string | null>>;
  impersonatedUserEmail: string | null;
  getEffectiveUserId: () => string | undefined;
  startImpersonation: (userId: string, email?: string) => void;
  stopImpersonation: () => void;
  // New high-level variables for cleaner data access
  dataUserId: string; // ID of user whose data is being viewed (impersonated or acting)
  actingUserId: string; // ID of person actually logged in (never changes)
}

var sessionUserAttributes: any;
var cognitoUser: cognitoUser;

const AccountContext = createContext<CognitoUserData | null>(null);
const Account = (props: any) => {
  const [loggedInUserCognitoIdToken, setLoggedInUserCognitoIdToken] = useState<
    string | undefined
  >("");
  const [userCognitoAttrubutes, setUserCognitoAttrubutes] = useState<{}>({});
  const getSession = async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.getSession(
          (err: Error | null, session: CognitoUserSession | null) => {
            if (err) {
              reject();
            } else {
              resolve(session);
            }
          }
        );
      } else {
        reject();
      }
    });
  };

  const authenticate = async (Username: string, Password: string) => {
    return await new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username, Pool: UserPool });
      const authenticationDetails = new AuthenticationDetails({
        Username,
        Password,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          sessionUserAttributes = cognitoUser.getUserAttributes(function (
            err,
            result
          ) {
            if (err) {
              console.log("err", err);
              return;
            }
            window.localStorage.setItem("UserCognitoAttrubutes", JSON.stringify(result))
            // window.localStorage.setItem("expireTime",Date.now() + (parseInt(process.env.REACT_APP_INACTIVITY_DELAY_SECONDS!) * 1000).toString())
            window.localStorage.setItem("Hello","Hi")
            setUserCognitoAttrubutes(result!);
          });
        },
        onFailure: (err) => {
          console.log("onFailure: ", err);
          reject(err);
        },
        newPasswordRequired: () => {
          cognitoUser.completeNewPasswordChallenge(
            process.env.REACT_APP_DEFAULT_PWD!,
            sessionUserAttributes,
            {
              onSuccess: (result) => {
                // login
              },
              onFailure: (result) => {
                // login
              },
            }
          );
        },
      });
    });
  };

  const getRefreshedToken = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session) {
            if ((err as any)?.code === "NotAuthorizedException") {
              window.dispatchEvent(new CustomEvent("cognitoSessionExpired"));
            }
            reject(err || new Error("Session expired"));
          } else {
            resolve(session.getIdToken().getJwtToken());
          }
        });
      } else {
        reject(new Error("No authenticated user"));
      }
    });
  };

  const [myAccountOpen, setMyAccountOpen] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<userSubscriptionData | null>(null)
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [impersonatedUserEmail, setImpersonatedUserEmail] = useState<string | null>(null);

  // Restore impersonation state from localStorage on mount
  useEffect(() => {
    const storedImpersonatedUserId = localStorage.getItem('impersonatedUserId');
    if (storedImpersonatedUserId) {
      setImpersonatedUserId(storedImpersonatedUserId);
      setImpersonatedUserEmail(localStorage.getItem('impersonatedUserEmail'));
      console.log(`Restored impersonation of user: ${storedImpersonatedUserId}`);
    }
  }, []);

  // Get the effective user ID (impersonated or real)
  const getEffectiveUserId = (): string | undefined => {
    if (impersonatedUserId) {
      return impersonatedUserId;
    }
    return (userCognitoAttrubutes as cognitoUserAttributes).sub;
  };

  // Computed values for cleaner data access throughout the app
  // dataUserId: The ID of the user whose data is being viewed (impersonated user or acting user)
  const dataUserId = (impersonatedUserId || (userCognitoAttrubutes as cognitoUserAttributes).sub) as string;

  // actingUserId: The ID of the person actually logged in (never changes during impersonation)
  const actingUserId = (userCognitoAttrubutes as cognitoUserAttributes).sub as string;

  // Start impersonating a user
  const startImpersonation = (userId: string, email?: string) => {
    console.log(`Starting impersonation of user: ${userId}`);
    setImpersonatedUserId(userId);
    setImpersonatedUserEmail(email ?? null);
    // Store in localStorage so it persists across page refreshes
    localStorage.setItem('impersonatedUserId', userId);
    if (email) {
      localStorage.setItem('impersonatedUserEmail', email);
    } else {
      localStorage.removeItem('impersonatedUserEmail');
    }
  };

  // Stop impersonating and return to real user
  const stopImpersonation = () => {
    console.log('Stopping impersonation');
    setImpersonatedUserId(null);
    setImpersonatedUserEmail(null);
    localStorage.removeItem('impersonatedUserId');
    localStorage.removeItem('impersonatedUserEmail');
  };

  const logout = () => {
    const user = UserPool.getCurrentUser();
    setLoggedInUserCognitoIdToken("");
    // Clear impersonation on logout
    stopImpersonation();
    if (user) {
      user.signOut();
    }
  };

  return (
    <AccountContext.Provider
      value={{
        authenticate,
        getSession,
        getRefreshedToken,
        logout,
        userCognitoAttrubutes,
        setUserCognitoAttrubutes,
        loggedInUserCognitoIdToken,
        setLoggedInUserCognitoIdToken,
        myAccountOpen,
        setMyAccountOpen,
        subscriptionDetails,
        setSubscriptionDetails,
        impersonatedUserId,
        setImpersonatedUserId,
        impersonatedUserEmail,
        getEffectiveUserId,
        startImpersonation,
        stopImpersonation,
        dataUserId,
        actingUserId,
      }}
    >
      {props.children}
    </AccountContext.Provider>
  );
};

function useAccountContext() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error("useLoginContext must be used within LoginContextProvider");
  }
  return context;
}

export { Account, useAccountContext };
